import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, } from 'react-router-dom';
import api from '../utils/api';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import QuotationPreviewModal from '../components/QuotationPreviewModal';
import { 
    FileText, 
    ArrowLeft, 
    Pencil, 
    Download, 
    Loader2, 
    Printer, 
    Trash2, 
    Building2, 
    User, 
    Palette, 
    ClipboardList, 
    IndianRupee, 
    ScrollText 
} from 'lucide-react';
// import './ViewProfessionalQuotation.css';

const ViewProfessionalQuotation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quotation, setQuotation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        fetchQuotation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchQuotation = async () => {
        try {
            const response = await api.get(`/professional-quotations/${id}`);
            setQuotation(response.data.quotation);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load quotation');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            setDownloading(true);
            const response = await api.get(`/professional-quotations/${id}/download`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Project-Assurance-${quotation.quotationNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setError('Failed to download PDF');
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this Project Assurance quotation?')) {
            try {
                await api.delete(`/professional-quotations/${id}`);
                navigate('/quotations');
            } catch (err) {
                setError('Failed to delete quotation');
            }
        }
    };

    const handleEditClick = () => {
        setShowPreviewModal(true);
    };

    const handleModalClose = (saved) => {
        setShowPreviewModal(false);
        if (saved) {
            // Refresh quotation data after saving
            fetchQuotation();
        }
    };

    if (loading) return <Loading />;
    if (!quotation) return <Alert type="error" message="Quotation not found" />;

    return (
        <div className="view-professional-quotation-container">
            {showPreviewModal && (
                <QuotationPreviewModal
                    quotationId={id}
                    onClose={handleModalClose}
                />
            )}

            <div className="quotation-view">
                {/* Header */}
                <div className="page-header" style={{ marginBottom: '30px' }}>
                    <div>
                        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FileText size={28} /> Project Assurance Details
                        </h1>
                        <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>
                            Quotation #: {quotation.quotationNumber} | Status: <span style={{
                                padding: '4px 12px',
                                borderRadius: '20px',
                                background: quotation.status === 'draft' ? '#fef3c7' : quotation.status === 'sent' ? '#dbeafe' : quotation.status === 'accepted' ? '#d1fae5' : '#fee2e2',
                                color: quotation.status === 'draft' ? '#92400e' : quotation.status === 'sent' ? '#1e40af' : quotation.status === 'accepted' ? '#065f46' : '#991b1b',
                                fontSize: '13px',
                                fontWeight: '500'
                            }}>{quotation.status.toUpperCase()}</span>
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate('/quotations')}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <ArrowLeft size={18} /> Back
                        </button>
                        <button
                            onClick={handleEditClick}
                            className="btn btn-primary"
                            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <Pencil size={18} /> Edit
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleDownloadPDF}
                            disabled={downloading}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            {downloading ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                            {downloading ? 'Downloading...' : 'Download PDF'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handlePrint}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <Printer size={18} /> Print
                        </button>
                        <button
                            type="button"
                            className="btn"
                            onClick={handleDelete}
                            style={{ background: '#ef4444', color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <Trash2 size={18} /> Delete
                        </button>
                    </div>
                </div>

                {error && <Alert type="error" message={error} onClose={() => setError('')} />}

                {/* Quotation Date */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">Quotation Information</div>
                    <div className="card-body">
                        <div className="form-row">
                            <div>
                                <strong>Quotation Number:</strong> {quotation.quotationNumber}
                            </div>
                            <div>
                                <strong>Date:</strong> {new Date(quotation.quotationDate).toLocaleDateString()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Project Information */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Building2 size={18} className="anim-pulse" /> Project Information
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <strong>Project Type:</strong> {quotation.projectDetails.projectType}
                            </div>
                            <div className="info-item">
                                <strong>Project Name:</strong> {quotation.projectDetails.projectName}
                            </div>
                            <div className="info-item">
                                <strong>Project Location:</strong> {quotation.projectDetails.projectLocation}
                            </div>
                            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                <strong>Physical Address:</strong> {quotation.projectDetails.projectAddress}
                            </div>
                            {quotation.projectDetails.projectArea > 0 && (
                                <div className="info-item">
                                    <strong>Project Area:</strong> {quotation.projectDetails.projectArea} Sqft
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Client Information */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <User size={18} /> Client Information
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <strong>Name:</strong> {quotation.clientDetails.name}
                            </div>
                            <div className="info-item">
                                <strong>Cell Phone:</strong> {quotation.clientDetails.cellPhone}
                            </div>
                            {quotation.clientDetails.email && (
                                <div className="info-item">
                                    <strong>Email:</strong> {quotation.clientDetails.email}
                                </div>
                            )}
                            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                <strong>Physical Address:</strong> {quotation.clientDetails.physicalAddress}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Designer Information */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Palette size={18} /> Designer/Architect Information
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <strong>Firm Name:</strong> {quotation.designerDetails.firmName}
                            </div>
                            <div className="info-item">
                                <strong>Head Designer:</strong> {quotation.designerDetails.headDesignerName}
                            </div>
                            {quotation.designerDetails.credentials && (
                                <div className="info-item">
                                    <strong>Credentials:</strong> {quotation.designerDetails.credentials}
                                </div>
                            )}
                            {quotation.designerDetails.licenseNumber && (
                                <div className="info-item">
                                    <strong>License Number:</strong> {quotation.designerDetails.licenseNumber}
                                </div>
                            )}
                            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                <strong>Physical Address:</strong> {quotation.designerDetails.physicalAddress}
                            </div>
                            <div className="info-item">
                                <strong>Cell Phone:</strong> {quotation.designerDetails.cellPhone}
                            </div>
                            <div className="info-item">
                                <strong>Email:</strong> {quotation.designerDetails.email}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Work Stages */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ClipboardList size={18} /> Work Stages
                        </div>
                    </div>
                    <div className="card-body">
                        {quotation.workStages.map((stage, index) => (
                            <div key={index} style={{ marginBottom: '15px', padding: '15px', background: '#f9fafb', borderRadius: '8px' }}>
                                <strong>Work Stage {stage.stageNumber}: {stage.stageName}</strong>
                                {stage.description && (
                                    <p style={{ marginTop: '8px', color: '#666' }}>{stage.description}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Charges */}
                {quotation.charges && quotation.charges.stages && quotation.charges.stages.length > 0 && (
                    <div className="card" style={{ marginBottom: '25px' }}>
                        <div className="card-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <IndianRupee size={18} /> Charges
                            </div>
                        </div>
                        <div className="card-body">
                            {quotation.charges.stages.map((charge, index) => (
                                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #e5e7eb' }}>
                                    <span>{charge.stageName}</span>
                                    <span style={{ fontWeight: '600' }}>
                                        ₹{charge.amount.toLocaleString('en-IN')} {charge.percentage && `(${charge.percentage})`}
                                    </span>
                                </div>
                            ))}
                            {quotation.charges.additionalNotes && (
                                <div style={{ marginTop: '15px', padding: '12px', background: '#fef3c7', borderRadius: '6px', color: '#92400e' }}>
                                    {quotation.charges.additionalNotes}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Additional Details */}
                <div className="card" style={{ marginBottom: '25px' }}>
                    <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <ScrollText size={18} /> Additional Details
                        </div>
                    </div>
                    <div className="card-body">
                        {quotation.inceptionOfWork && (
                            <div style={{ marginBottom: '20px' }}>
                                <strong>2.3 Inception of Work:</strong>
                                {(quotation.inceptionOfWork.totalFees > 0 || quotation.inceptionOfWork.perSqftRate > 0) && (
                                    <div style={{ marginTop: '10px', padding: '12px', background: '#f0f9ff', borderRadius: '6px' }}>
                                        <div style={{ display: 'flex', gap: '30px', marginBottom: '8px' }}>
                                            {quotation.inceptionOfWork.totalFees > 0 && (
                                                <div><strong>Total Fees:</strong> ₹{quotation.inceptionOfWork.totalFees.toLocaleString('en-IN')}</div>
                                            )}
                                            {quotation.inceptionOfWork.perSqftRate > 0 && (
                                                <div><strong>Per Sqft Rate:</strong> ₹{quotation.inceptionOfWork.perSqftRate}/sqft</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {quotation.inceptionOfWork.notes && (
                                    <p style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>{quotation.inceptionOfWork.notes}</p>
                                )}
                            </div>
                        )}

                        {quotation.generalAccord && quotation.generalAccord.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <strong>General Accord:</strong>
                                <ul style={{ marginTop: '8px' }}>
                                    {quotation.generalAccord.map((point, index) => (
                                        <li key={index} style={{ marginBottom: '5px' }}>{point}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {quotation.designServices && quotation.designServices.length > 0 && (
                            <div style={{ marginBottom: '20px' }}>
                                <strong>Design Services:</strong>
                                <ul style={{ marginTop: '8px' }}>
                                    {quotation.designServices.map((service, index) => (
                                        <li key={index} style={{ marginBottom: '5px' }}>{service}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {quotation.closeOutPeriod && (
                            <div>
                                <strong>Close Out Period:</strong>
                                {quotation.closeOutMonths > 0 && (
                                    <div style={{ marginTop: '5px', color: '#0284c7', fontWeight: '600' }}>
                                        Duration: {quotation.closeOutMonths} Month{quotation.closeOutMonths !== 1 ? 's' : ''}
                                    </div>
                                )}
                                <p style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>{quotation.closeOutPeriod}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProfessionalQuotation;

